import React, { useRef } from 'react';
import clsx from 'clsx';

import styles from '@danske/sapphire-css/components/toast/toast.module.css';
import { Toast } from './Toast';
import { ToastContext, ToastData } from './context';
import { SapphireStyleProps } from '../../utils';
import { useThemeContext } from '../../SapphireProvider';

import {
  UNSTABLE_ToastRegion as AriaToastRegion,
  UNSTABLE_ToastList as AriaToastList,
} from 'react-aria-components';
import { useEvent } from '@react-aria/utils';
import { useInteractOutside } from '@react-aria/interactions';
import { Overlay } from '@react-aria/overlays';

type VerticalPlacement = 'top' | 'bottom';
type HorizontalPlacement = 'left' | 'right';

export type ToastRegionProps = SapphireStyleProps & {
  /**
   * Placement of the Toast notifications on the page.
   */
  placement?: VerticalPlacement | `${VerticalPlacement}-${HorizontalPlacement}`;
};

export const ToastRegion = <T extends ToastData>({
  placement = 'bottom',
  ...props
}: ToastRegionProps): React.JSX.Element => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { overlayZIndex = 100 } = useThemeContext();

  const { queue, overlayState } = React.useContext(ToastContext)!;

  useInteractOutside({
    ref,
    onInteractOutside: overlayState.isOpen
      ? () => {
          overlayState.close();
        }
      : undefined,
  });

  useEvent(
    ref,
    'keydown',
    overlayState.isOpen
      ? (e) => {
          if (e.key === 'Escape') {
            overlayState.close();
          }
        }
      : undefined
  );

  return (
    <Overlay>
      <AriaToastRegion
        queue={queue}
        {...props}
        ref={ref}
        className={clsx(
          styles['sapphire-toast-container'],
          styles[`sapphire-toast-container--${placement}`]
        )}
        style={
          {
            zIndex: overlayZIndex + 200,
          } /* above overlays from @danske/sapphire-react */
        }
      >
        <AriaToastList<T>
          className={({ isHovered }) =>
            clsx(
              styles['sapphire-toast-list'],
              styles['js-hover'],
              overlayState.isOpen
                ? [styles['sapphire-toast-list--expanded']]
                : [styles['sapphire-toast-list--collapsed']],
              {
                [styles['is-hover']]: isHovered,
              }
            )
          }
        >
          {({ toast }) => (
            <Toast
              zIndex={overlayZIndex + 200}
              key={toast.key}
              placement={placement.includes('top') ? 'top' : 'bottom'}
              toast={toast}
            />
          )}
        </AriaToastList>
      </AriaToastRegion>
    </Overlay>
  );
};
